import trtc, { LocalStream, StreamConfig, StreamEventMap } from 'trtc-js-sdk';
import memoize from 'lodash/memoize';
import { Span } from '@opentelemetry/api';
import logger from '../common/Logger';

export const createLocalStream = memoize(
  // 这里的sdkAppId是座席的，不是大账号的, TRTC创建本地流实际上不依赖sdkAppId，只是作为日志记录
  async (options: StreamConfig & { span?: Span; sdkAppId: string }) => {
    logger.info('createLocalStream', `audio: ${options.audio}`, `video: ${options.video}`);
    try {
      options?.span?.addEvent('beforeCreateLocalStream');
      const startTime = new Date().getTime();
      const localStream = trtc.createStream(options);
      if (options.video) {
        // 设置视频属性 Profile 为 720p
        localStream.setVideoProfile('720p');
        options?.span?.addEvent('setVideoProfile(720p)');
      }
      await localStream.initialize();
      if (options.video) {
        localStream.setVideoContentHint('detail');
        options?.span?.addEvent('setVideoContentHint(detail)');
      }
      const endTime = new Date().getTime();
      const duration = startTime - endTime;
      if (duration > 10000) {
        logger.report('createStream initialization time is too long', `duration: ${duration}`);
      }
      localStream.on('player-state-changed', playerStateChangedEvent);
      document.addEventListener('visibilitychange', visibilityChange);
      // @ts-ignore
      localStream.on('error', streamError);
      options?.span?.addEvent('localStream.initialize', { duration });
      return localStream;
    } catch (e) {
      options?.span?.recordException(e as Error);
      logger.warn('localStream initialize failed', e);
      // 视频客服初始化失败，重新初始化
      if (options.video) {
        options?.span?.addEvent('recreateLocalStream');
        const localStream = trtc.createStream({ ...options, video: false });
        // 设置视频属性 Profile 为 720p
        localStream.setVideoProfile('720p');
        options?.span?.addEvent('setVideoProfile(720p)');
        await localStream.initialize();
        localStream.setVideoContentHint('detail');
        options?.span?.addEvent('setVideoContentHint(detail)');
        options?.span?.addEvent('recreateLocalStream success');
        logger.warn('rebuild localStream success');
        localStream.on('player-state-changed', playerStateChangedEvent);
        document.addEventListener('visibilitychange', visibilityChange);
        // @ts-ignore
        localStream.on('error', streamError);
        return localStream;
      }
      throw e;
    }
  },
  ({ video, userId, sdkAppId }) => `${sdkAppId}-${userId}-${video}`,
);

export const closeLocalStream = (userInfo: { sdkAppId: string; userId: string }) => {
  const keys = [`${userInfo.sdkAppId}-${userInfo.userId}-${true}`, `${userInfo.sdkAppId}-${userInfo.userId}-${false}`];
  keys.forEach((key) => {
    createLocalStream.cache.get(key)?.then((stream: LocalStream) => {
      stream?.close();
      createLocalStream.cache.delete(key);
    });
  });
};

const playerStateChangedEvent = (event: StreamEventMap['player-state-changed']) => {
  logger.info('player-state-changed', event, `current visibility state: ${document.visibilityState}`);
};

const visibilityChange = () => {
  logger.info(`visibility change: ${document.visibilityState}`);
};

const streamError = (event: any) => {
  const code = event?.getCode?.();
  logger.error('stream error', code);
};
